/* eslint-disable no-empty */
import Box from '@material-ui/core/Box';
import Container from '@material-ui/core/Container';
import React, { Suspense, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router';
import {
  useRecoilState,
  useRecoilValue,
  useResetRecoilState,
  useSetRecoilState,
} from 'recoil';
import Delayed from '~/src/components/Delayed';
import LoaderOverlay from '~/src/components/LoaderOverlay';
import ProjectMenuDialogs from '~/src/Projects/components/ProjectMenuDialogs';
import useGetProjectFileNames from '~/src/Files/hooks/useGetProjectFileNames';
import {
  selectedFileNameState,
  selectedProductionFileNameState,
  selectedSourceState,
  Sources,
} from '~/src/Files/store';
import useFirestoreAuth from '~/src/Firestore/hooks/useFirestoreAuth';
import useFirestoreToken from '~/src/Firestore/hooks/useFirestoreToken';
import HelperButton from '~/src/Helper/components/HelperButton';
import useProjectHistory from '~/src/HistoryDialog/hooks/useProjectHistory';
import { isOutdatedProjectSelector, Job } from '~/src/HistoryDialog/store';
import useProjectId from '~/src/hooks/useProjectId';
import useTitle from '~/src/hooks/useTitle';
import ViewSwitcher from '~/src/ProjectResults/components/ViewSwitcher';
import {
  correlationAnalysisState,
  selectedViewState,
} from '~/src/ProjectResults/store';
import useFind from '~/src/Projects/hooks/useFind';
import useFindOne from '~/src/Projects/hooks/useFindOne';
import useFindOneOnUpdate from '~/src/Projects/hooks/useFindOneOnUpdate';
import {
  ConditionControl,
  copyIssueSelector,
  entryFileSelector,
  entryProjectSelector,
  isCopyingSelector,
  isFastTrainedProjectSelector,
  pendingFind,
} from '~/src/Projects/store';
import StepPanel, {
  PanelIndicatorSteps,
} from '~/src/StepPanel/components/StepPanel';
import useDetectRegressionOutliers from '../hooks/useDetectRegressionOutliers';
import useProjectState from '../hooks/useProjectState';
import useResetProject from '../hooks/useResetProject';
import useScriptStatus from '../hooks/useScriptStatus';
import {
  correlationAnalysisReportState,
  currentProjectIdState,
  detectOutliersDoneState,
  displayName,
  driftDetectionResultState,
  isPredictionDoneState,
  isSkippedProductionFileState,
  panelIndicatorState,
  projectIdState,
  ProjectState,
  projectState,
  ProjectType,
  projectType,
  projectVersionState,
  ScriptNames,
} from '../store';
import CopyingDialog from './CopyingDialog';
import CopyIssueWarning from './CopyIssueWarning';
import FastTrainingWarning from './FastTrainingWarning';
import OutdatedWarning from './OutdatedWarning';
import ProjectHeader from './ProjectHeader';
import useDetectCorrelationAnalysis from '../hooks/useDetectCorrelationAnalysis';
import useDriftDetectionAnalysis from '../hooks/useDriftDetectionAnalysis';

const BinaryProject = React.lazy(() => import('./BinaryProject'));
const TimeSeriesProject = React.lazy(() => import('./TimeSeriesProject'));

const Project = () => {
  const { push } = useHistory();
  const projectId = useProjectId();
  const [name, setName] = useRecoilState(displayName);
  const { getDriftDetectionFile } = useDriftDetectionAnalysis();
  const resetDriftDetectionResult = useResetRecoilState(
    driftDetectionResultState,
  );
  const isPredictionDone = useRecoilValue(isPredictionDoneState(projectId));
  const detectOutliersDone = useRecoilValue(detectOutliersDoneState(projectId));
  const correlationAnalysisReport = useRecoilValue(
    correlationAnalysisReportState(projectId),
  );
  const resetCorrelationAnalysisState = useResetRecoilState(
    correlationAnalysisState,
  );

  const setCorrelationAnalysisState = useSetRecoilState(
    correlationAnalysisState,
  );

  const setVersion = useSetRecoilState(projectVersionState);
  const [type, setType] = useRecoilState(projectType);
  const setCurrentProjectId = useSetRecoilState(currentProjectIdState);
  const setSelectedSource = useSetRecoilState(selectedSourceState);
  const setProjectIdState = useSetRecoilState(projectIdState);
  const [state, setState] = useRecoilState(projectState);
  const [selectedView, setSelectedView] = useRecoilState(selectedViewState);
  const projectLoading = useRecoilValue(pendingFind);
  const entryProject = useRecoilValue(entryProjectSelector(projectId));
  const resetProject = useResetProject();
  useTitle(name);
  const trainingFileName = useRecoilValue(selectedFileNameState);
  const productionFileName = useRecoilValue(selectedProductionFileNameState);
  const isSkippedProductionFile = useRecoilValue(
    isSkippedProductionFileState(projectId),
  );
  const panelIndicator = useRecoilValue(panelIndicatorState);
  const [findOne] = useFindOne(projectId);
  const { getOutliersFile } = useDetectRegressionOutliers();

  const { getCorrelationAnalysisFile } = useDetectCorrelationAnalysis();

  const [getProjectFileNames] = useGetProjectFileNames();
  const isReady = useMemo(
    () => [ProjectState.Ready, ProjectState.Done].includes(state),
    [state],
  );
  const [find] = useFind();
  const entryFile = useRecoilValue(entryFileSelector(projectId));
  const isOutdatedProject = useRecoilValue(
    isOutdatedProjectSelector(projectId),
  );
  const fastTrainedProject = useRecoilValue(
    isFastTrainedProjectSelector(projectId),
  );
  const { getHistory } = useProjectHistory(projectId);
  const automodelerStatus = useScriptStatus(ScriptNames.Automodeler);
  const isCopying = useRecoilValue(isCopyingSelector(projectId));
  const copyIssue = useRecoilValue(copyIssueSelector(projectId));

  useFirestoreToken();
  useFirestoreAuth();
  useProjectState(projectId);
  useFindOneOnUpdate(projectId);

  const handleClose = () => {
    push('/projects');
    if (
      entryFile?.trainingFile?.connectorType
        !== entryProject?.files?.training.connectorType
      || entryFile?.productionFile?.connectorType
        !== entryProject?.files?.production.connectorType
    ) {
      find();
    }
  };

  const handleStepPanelClick = (redirectState: ProjectState) => {
    if (
      !trainingFileName
      || (!productionFileName && redirectState === ProjectState.Ready)
    ) {
      getProjectFileNames();
      setSelectedSource(Sources.Connectors);
    }
    setState(redirectState);
  };

  useEffect(() => {
    if (detectOutliersDone?.state === 'ready') {
      getOutliersFile(projectId);
    }
  }, [detectOutliersDone?.state, projectId]);

  useEffect(() => {
    if (correlationAnalysisReport?.state === 'ready') {
      getCorrelationAnalysisFile(projectId);
    } else {
      setCorrelationAnalysisState((prev) => ({
        ...prev,
        data: null,
        targetColumn: null,
      }));
    }
  }, [projectId, correlationAnalysisReport?.state]);

  useEffect(() => {
    const setCurrentProject = (project) => {
      if (project) {
        setCurrentProjectId(project._id);
        setName(project.displayName);
        setType(project.type);
        setState(project.state);
        setVersion(project.version || 'v1');
      }
    };

    const getProject = async () => {
      const data = await findOne();
      if (!data) {
        push('/projects');
      } else {
        setCurrentProject(data.project);
      }
    };

    if (entryProject) {
      setCurrentProject(entryProject);
    }
    // Always refetch on mount so a cached entry doesn't hide a copyStatus
    // (or other) change that happened while the page wasn't open.
    getProject();

    getHistory(0, Job.Analyzing, 1); // get last analyzing history

    setProjectIdState(projectId);

    return resetProject;
  }, [projectId]);

  useEffect(() => {
    if (!trainingFileName && entryProject && type) {
      getProjectFileNames();
      setSelectedSource(Sources.Connectors);
    }
  }, [entryProject, type]);

  useEffect(() => {
    if (isPredictionDone && projectId) {
      getDriftDetectionFile(projectId);
    }

    return () => {
      resetDriftDetectionResult();
    };
  }, [isPredictionDone, projectId]);

  useEffect(
    () => () => {
      resetCorrelationAnalysisState();
    },
    [],
  );

  useEffect(() => {
    if (!isCopying) return undefined;

    const interval = setInterval(() => {
      findOne();
    }, 5000);

    return () => clearInterval(interval);
  }, [isCopying, projectId]);

  return (
    <Box
      display="flex"
      flexDirection="column"
      position="relative"
      flex="auto"
      mb={25}
    >
      {projectLoading && (
        <Delayed>
          <LoaderOverlay />
        </Delayed>
      )}
      <Container>
        <ProjectMenuDialogs />
        <ProjectHeader handleClose={handleClose} />
        {copyIssue && <CopyIssueWarning status={copyIssue} />}
        {isOutdatedProject && <OutdatedWarning />}
        {fastTrainedProject === ConditionControl.Enabled
          && automodelerStatus.isScriptDone && <FastTrainingWarning />}
        <Box display="flex" alignItems="center" justifyContent="space-between">
          {type && (
            <StepPanel
              projectState={state}
              onCancel={handleClose}
              type={type}
              realState={entryProject?.state || state}
              onClick={handleStepPanelClick}
              skippedStep={
                (isSkippedProductionFile
                  && PanelIndicatorSteps.ProductionFile)
                || null
              }
              panelIndicator={panelIndicator}
            />
          )}
          {isReady && (
            <ViewSwitcher onSelect={setSelectedView} selected={selectedView} />
          )}
        </Box>
        <Suspense fallback={<LoaderOverlay />}>
          {type
            && (type === ProjectType.TimeSeriesForecast ? (
              <TimeSeriesProject />
            ) : (
              <BinaryProject />
            ))}
        </Suspense>
      </Container>
      <HelperButton />
      <CopyingDialog open={isCopying} onPrimaryClick={handleClose} />
    </Box>
  );
};

export default React.memo(Project);
